import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  iconoirCart,
  iconoirCloudUpload,
  iconoirRefresh,
} from '@ng-icons/iconoir';
import { finalize, Subscription } from 'rxjs';
import { ButtonDirective } from '../../../components/button/button.directive';
import { LoadingSpinnerComponent } from '../../../components/loading-spinner/loading-spinner.component';
import { ModalComponent } from '../../../components/modal/modal.component';
import { SelectComponent } from '../../../components/select/select.component';
import { TabLayoutComponent } from '../../../components/tab-layout/tab-layout.component';
import { TabComponent } from '../../../components/tab/tab.component';
import { APITableComponent } from '../../../components/table/api-table/api-table.component';
import { StaticTableComponent } from '../../../components/table/static-table/static-table.component';
import {
  FilterOption,
  ResponseData,
  TableRow,
} from '../../../components/table/table.interface';
import {
  Distributor,
  SalesSheetStatus,
  SalesSheetType,
} from '../../../interfaces/apis/sales-sheet';
import { SalesSheetService } from '../../../services/sales-sheet.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { SalesSheetStatusProgressComponent } from './sales-sheet-status-progress/sales-sheet-status-progress.component';

const SALES_SHEET_MAX_SIZE = 10 * 1024 * 1024;
const SALES_SHEET_EXTENSIONS = ['xlsx', 'xls', 'csv'];
const SALES_SHEET_POLL_INTERVAL = 10_000;

@Component({
  selector: 'app-sales-sheet-tab',
  imports: [
    CommonModule,
    APITableComponent,
    ButtonDirective,
    LoadingSpinnerComponent,
    ModalComponent,
    NgIcon,
    SelectComponent,
    SalesSheetStatusProgressComponent,
    StaticTableComponent,
    TabLayoutComponent,
  ],
  providers: [
    { provide: TabComponent, useExisting: SalesSheetTabComponent },
    provideIcons({ iconoirCart, iconoirCloudUpload, iconoirRefresh }),
  ],
  templateUrl: './sales-sheet-tab.component.html',
  styleUrl: './sales-sheet-tab.component.scss',
})
export class SalesSheetTabComponent extends TabComponent implements OnDestroy {
  override name = this.dictionaryService.get('SalesSheets');

  @ViewChild('salesTable') private salesTable?: APITableComponent;
  @ViewChild('fileInput') private fileInput?: ElementRef<HTMLInputElement>;

  readonly dictionary = this.dictionaryService;
  private readonly salesSheetService = inject(SalesSheetService);
  private readonly snackbar = inject(SnackbarService);

  readonly uploadModalOpen = signal(false);
  readonly distributors = signal<Distributor[]>([]);
  readonly distributorsLoading = signal(false);
  readonly distributorsError = signal(false);
  readonly selectedDistributorId = signal<string | null>(null);
  readonly selectedSheetType = signal<SalesSheetType | null>(null);
  readonly selectedFile = signal<File | null>(null);
  readonly fileError = signal<string | null>(null);
  readonly submitAttempted = signal(false);
  readonly uploading = signal(false);
  readonly invalidRowsModalOpen = signal(false);
  readonly invalidRowsLoading = signal(false);
  readonly invalidRowsError = signal<string | null>(null);
  readonly invalidRowsData = signal<ResponseData | null>(null);
  readonly selectedInvalidRowsSheetId = signal<string | null>(null);
  readonly selectedInvalidRowsSheetName = signal<string | null>(null);

  readonly distributorNames = computed(() =>
    this.distributors().map((distributor) => distributor.name),
  );
  readonly distributorIds = computed(() =>
    this.distributors().map((distributor) => distributor.id),
  );
  readonly sheetTypeOptions = Object.values(SalesSheetType);
  readonly canSubmit = computed(
    () =>
      !!this.selectedDistributorId() &&
      !!this.selectedSheetType() &&
      !!this.selectedFile() &&
      !this.fileError() &&
      !this.distributorsLoading() &&
      !this.uploading(),
  );
  readonly invalidRowsModalTitle = computed(() => {
    const title = this.dictionary.get('InvalidRows');
    const sheetName = this.selectedInvalidRowsSheetName();
    return sheetName ? `${title} - ${sheetName}` : title;
  });

  private pollTimer?: ReturnType<typeof setTimeout>;
  private invalidRowsSubscription?: Subscription;

  override async main(): Promise<void> {
    this.schedulePolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.invalidRowsSubscription?.unsubscribe();
  }

  openUploadModal(): void {
    this.resetUploadForm();
    this.uploadModalOpen.set(true);
    this.loadDistributors();
  }

  closeUploadModal(): void {
    this.uploadModalOpen.set(false);
    this.resetUploadForm();
  }

  loadDistributors(): void {
    if (this.distributorsLoading()) return;

    this.distributorsLoading.set(true);
    this.distributorsError.set(false);

    this.salesSheetService
      .getCompanyDistributors()
      .pipe(finalize(() => this.distributorsLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.distributors.set([]);
            this.distributorsError.set(true);
            return;
          }
          this.distributors.set(response.data?.distributors ?? []);
        },
        error: () => {
          this.distributors.set([]);
          this.distributorsError.set(true);
        },
      });
  }

  selectDistributor(option: FilterOption): void {
    this.selectedDistributorId.set(option.id);
  }

  selectSheetType(option: FilterOption): void {
    this.selectedSheetType.set(option.id as SalesSheetType);
  }

  selectFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.fileError.set(null);

    if (!file) {
      this.selectedFile.set(null);
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!SALES_SHEET_EXTENSIONS.includes(extension)) {
      this.selectedFile.set(null);
      this.fileError.set(this.dictionary.get('SalesSheetUnsupportedFile'));
      input.value = '';
      return;
    }

    if (file.size > SALES_SHEET_MAX_SIZE) {
      this.selectedFile.set(null);
      this.fileError.set(this.dictionary.get('SalesSheetFileTooLarge'));
      input.value = '';
      return;
    }

    this.selectedFile.set(file);
  }

  removeFile(): void {
    this.selectedFile.set(null);
    this.fileError.set(null);
    if (this.fileInput) this.fileInput.nativeElement.value = '';
  }

  formatFileSize(file: File): string {
    if (file.size < 1024 * 1024) {
      return `${Math.max(1, Math.round(file.size / 1024))} KB`;
    }
    return `${(file.size / (1024 * 1024)).toFixed(1)} MB`;
  }

  submitUpload(): void {
    this.submitAttempted.set(true);
    if (!this.canSubmit()) return;

    const distributorId = this.selectedDistributorId();
    const sheetType = this.selectedSheetType();
    const file = this.selectedFile();
    if (!distributorId || !sheetType || !file) return;

    this.uploading.set(true);
    this.salesSheetService
      .upload(distributorId, sheetType, file)
      .pipe(finalize(() => this.uploading.set(false)))
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.snackbar.showError(
              response.message || this.dictionary.get('SalesSheetUploadFailed'),
            );
            return;
          }

          this.snackbar.showSuccess(response.message);
          this.closeUploadModal();
          this.refreshTable(true);
        },
        error: (error) => {
          this.snackbar.showError(
            error?.error?.message || this.dictionary.get('SalesSheetUploadFailed'),
          );
        },
      });
  }

  refreshTable(resetToFirstPage = false): void {
    if (resetToFirstPage) {
      this.salesTable?.tableComponent?.tableContext.setPage(1);
    }
    this.salesTable?.refreshCurrentView();
    this.schedulePolling();
  }

  openSheet = (row: TableRow): void => {
    const path = row.value['Path'];
    if (typeof path !== 'string' || !path.trim()) {
      this.snackbar.showError(this.dictionary.get('SalesSheetPathMissing'));
      return;
    }

    window.open(
      this.salesSheetService.createExcelViewerUrl(path),
      '_blank',
      'noopener,noreferrer',
    );
  };

  handleInvalidRowsAction(event: Event, onClick: () => void): void {
    event.stopPropagation();
    onClick();
  }

  openInvalidRows = (row: TableRow): void => {
    const salesSheetId = row.value['salesSheetId'];
    if (typeof salesSheetId !== 'string' || !salesSheetId.trim()) {
      this.snackbar.showError(
        this.dictionary.get('SalesSheetInvalidRowsUnavailable'),
      );
      return;
    }

    const sheetName = row.value['Name'];
    this.selectedInvalidRowsSheetId.set(salesSheetId);
    this.selectedInvalidRowsSheetName.set(
      typeof sheetName === 'string' && sheetName.trim() ? sheetName : null,
    );
    this.invalidRowsModalOpen.set(true);
    this.loadInvalidRows();
  };

  retryInvalidRows(): void {
    this.loadInvalidRows();
  }

  closeInvalidRowsModal(): void {
    this.invalidRowsSubscription?.unsubscribe();
    this.invalidRowsSubscription = undefined;
    this.invalidRowsModalOpen.set(false);
    this.invalidRowsLoading.set(false);
    this.invalidRowsError.set(null);
    this.invalidRowsData.set(null);
    this.selectedInvalidRowsSheetId.set(null);
    this.selectedInvalidRowsSheetName.set(null);
  }

  private resetUploadForm(): void {
    this.selectedDistributorId.set(null);
    this.selectedSheetType.set(null);
    this.selectedFile.set(null);
    this.fileError.set(null);
    this.submitAttempted.set(false);
    if (this.fileInput) this.fileInput.nativeElement.value = '';
  }

  private loadInvalidRows(): void {
    const salesSheetId = this.selectedInvalidRowsSheetId();
    if (!salesSheetId) return;

    this.invalidRowsSubscription?.unsubscribe();
    this.invalidRowsData.set(null);
    this.invalidRowsError.set(null);
    this.invalidRowsLoading.set(true);

    this.invalidRowsSubscription = this.salesSheetService
      .getInvalidRows(salesSheetId)
      .pipe(finalize(() => this.invalidRowsLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (!response.success || !response.data) {
            this.invalidRowsError.set(
              response.message ||
                this.dictionary.get('SalesSheetInvalidRowsLoadFailed'),
            );
            return;
          }

          this.invalidRowsData.set(response.data);
        },
        error: (error) => {
          this.invalidRowsError.set(
            error?.error?.message ||
              this.dictionary.get('SalesSheetInvalidRowsLoadFailed'),
          );
        },
      });
  }

  private schedulePolling(): void {
    this.stopPolling();
    this.pollTimer = setTimeout(() => this.pollIfNeeded(), SALES_SHEET_POLL_INTERVAL);
  }

  private pollIfNeeded(): void {
    const context = this.salesTable?.tableComponent?.tableContext;
    if (!context) {
      this.schedulePolling();
      return;
    }

    if (!context.isTableInitialized() || context.isLoading()) {
      this.schedulePolling();
      return;
    }

    if (context.isError()) return;

    const hasActiveSheet = context.tableData().rows.some((row) => {
      const status = row.value['Status'];
      return (
        status === SalesSheetStatus.PENDING ||
        status === SalesSheetStatus.PROCESSING
      );
    });

    if (!hasActiveSheet) return;

    this.salesTable?.refreshCurrentView();
    this.schedulePolling();
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = undefined;
    }
  }
}
